// ClientListFilter — client-side list filtering without server round-trips
// (ts-7 / ts-8).
//
// Used by workspaces_live (ts-7) to filter workspace rows and
// profile_live (ts-8) to filter repo rows.
//
// ## workspaces_live usage (ts-7)
//
//   Attach phx-hook="ClientListFilter" to the filter form's container.
//   Each filterable row: data-workspace-row with data-name, data-phase,
//   data-env-host attributes. The hook listens to form input events and
//   toggles `hidden` on rows that don't match.
//
// ## profile_live usage (ts-8)
//
//   Attach phx-hook="ClientListFilter" to the repos-list container.
//   Each filterable row: data-list-item with data-name attribute.
//   The search input: data-list-filter-input.

import type { LiveViewHook } from "./types";

export interface ClientListFilterState {
  _onInput: (e: Event) => void;
}

const formString = (data: FormData, key: string, fallback: string): string => {
  const value = data.get(key);
  return typeof value === "string" ? value : fallback;
};

// Hide every row under `el` that the current filter values reject. Reads
// the filter from the DOM each time, so it gives the same answer however
// the rows got there — typed into, or patched in by the server.
const applyFilter = (el: HTMLElement): void => {
  const form = el.querySelector("form");

  if (form) {
    // workspaces_live multi-filter mode
    const data = new FormData(form);
    const text = formString(data, "text", "").toLowerCase();
    const phase = formString(data, "phase", "all");
    const envHost = formString(data, "env_host", "all");

    el.querySelectorAll<HTMLElement>("[data-workspace-row]").forEach((row) => {
      const nameMatch =
        !text || (row.dataset["name"] ?? "").toLowerCase().includes(text);
      const phaseMatch = phase === "all" || row.dataset["phase"] === phase;
      const hostMatch = envHost === "all" || row.dataset["envHost"] === envHost;
      row.classList.toggle("hidden", !(nameMatch && phaseMatch && hostMatch));
    });
  } else {
    // profile_live simple text-filter mode
    const input = el.querySelector<HTMLInputElement>(
      "[data-list-filter-input]",
    );
    const text = (input?.value ?? "").toLowerCase();

    el.querySelectorAll<HTMLElement>("[data-list-item]").forEach((item) => {
      const name = (item.dataset["name"] ?? "").toLowerCase();
      item.classList.toggle("hidden", !!text && !name.includes(text));
    });
  }
};

export const ClientListFilter = {
  mounted(this: LiveViewHook<never> & ClientListFilterState) {
    const onInput = (): void => {
      applyFilter(this.el);
    };

    this.el.addEventListener("input", onInput);
    this._onInput = onInput;
  },

  // A row the server patches or inserts — `stream_insert` on every phase
  // change, on /workspaces — is rendered without the `hidden` class the
  // filter put on it, so it reappeared whatever the filter said. Re-apply
  // after every patch.
  updated(this: LiveViewHook<never> & ClientListFilterState) {
    applyFilter(this.el);
  },

  destroyed(this: LiveViewHook<never> & ClientListFilterState) {
    this.el.removeEventListener("input", this._onInput as EventListener);
  },
};
